import api from './axios';
import { AxiosError } from 'axios';

// Backend response wrapper interface
interface BackendResponse<T> {
  data: T;
  message: string;
  success: boolean;
  timestamp: string;
}

export enum UserRole {
  SUPER_ADMIN = 'super_admin',
  TENANT_ADMIN = 'tenant_admin',
  FARMER_ADMIN = 'farmer_admin',
  SUPERVISOR = 'supervisor',
  EMPLOYEE = 'employee',
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  tenantId: string | null;
  farmId: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserDto {
  name: string;
  email: string;
  password: string;
  role: UserRole;
  tenantId?: string; // Will be set by the backend based on the current user's tenant
  farmId?: string; // Farm assignment for farm-related roles
}

export interface UpdateUserDto {
  name?: string;
  email?: string;
  password?: string;
  role?: UserRole;
  isActive?: boolean;
  farmId?: string;
}

// Type guard to check if an object is a User
function isUser(obj: any): obj is User {
  return obj && typeof obj === 'object' && 'id' in obj && 'name' in obj && 'email' in obj;
}

export const fetchUsers = async (): Promise<User[]> => {
  try {
    const response = await api.get<BackendResponse<User[]>>('/users');
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data && Array.isArray(responseData.data)) {
      return responseData.data;
    }
    
    // Fallback: check if the response is directly an array
    if (Array.isArray(responseData)) {
      return responseData;
    }
    
    console.error('Invalid users response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

export const fetchUser = async (id: string): Promise<User> => {
  try {
    const response = await api.get<BackendResponse<User>>(`/users/${id}`);
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data) {
      return responseData.data;
    }
    
    // Fallback: check if responseData is directly a User object
    if (isUser(responseData)) {
      return responseData;
    }
    
    console.error('Invalid user response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error(`Error fetching user ${id}:`, error);
    throw error;
  }
};

export const createUser = async (user: CreateUserDto): Promise<User> => {
  try {
    const response = await api.post<BackendResponse<User>>('/users', user);
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data) {
      return responseData.data;
    }
    
    // Fallback: check if responseData is directly a User object
    if (isUser(responseData)) {
      return responseData;
    }
    
    console.error('Invalid create user response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error('Error creating user:', error);
    if (error instanceof AxiosError && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

export const updateUser = async (id: string, user: UpdateUserDto): Promise<User> => {
  try {
    const response = await api.patch<BackendResponse<User>>(`/users/${id}`, user);
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (responseData && responseData.data) {
      return responseData.data;
    }
    
    // Fallback: check if responseData is directly a User object
    if (isUser(responseData)) {
      return responseData;
    }
    
    console.error('Invalid update user response format:', responseData);
    throw new Error('Invalid response format from server');
  } catch (error) {
    console.error(`Error updating user ${id}:`, error);
    if (error instanceof AxiosError && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

export const deleteUser = async (id: string): Promise<void> => {
  try {
    await api.delete(`/users/${id}`);
  } catch (error) {
    console.error(`Error deleting user ${id}:`, error);
    if (error instanceof AxiosError && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

export const roleLabels: Record<UserRole, string> = {
  [UserRole.SUPER_ADMIN]: 'Super Admin',
  [UserRole.TENANT_ADMIN]: 'Tenant Admin',
  [UserRole.FARMER_ADMIN]: 'Farmer Admin',
  [UserRole.SUPERVISOR]: 'Supervisor',
  [UserRole.EMPLOYEE]: 'Employee',
};
